import { CheckCircle2, Clock, Download, Eye, FileText, ListChecks, Loader2, RotateCw, Trash2, Upload, XCircle, AlertTriangle } from "lucide-react";

const STATUS = {
  waiting: { icon: Clock, cls: "text-slate-300 bg-slate-800/60", label: "Waiting" },
  uploading: { icon: Upload, cls: "text-amber-300 bg-amber-950/60", label: "Uploading" },
  queued: { icon: Clock, cls: "text-slate-400 bg-slate-800/60", label: "Queued" },
  running: { icon: Loader2, cls: "text-cyan-300 bg-cyan-950/60", label: "Running", spin: true },
  review: { icon: ListChecks, cls: "text-amber-200 bg-amber-900/50", label: "Check numbers" },
  waiting_photos: { icon: Upload, cls: "text-cyan-200 bg-cyan-950/60", label: "Confirmed · waiting for photos" },
  match_review: { icon: Eye, cls: "text-amber-200 bg-amber-900/50", label: "Paused · review matches" },
  done: { icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-950/60", label: "Ready" },
  failed: { icon: XCircle, cls: "text-red-300 bg-red-950/60", label: "Failed" },
};

const fmt = new Intl.DateTimeFormat("en-AU", {
  timeZone: "Australia/Sydney", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
});

function IconBtn({ testId, title, onClick, children, className = "" }) {
  return (
    <button type="button" title={title} onClick={onClick} data-testid={testId}
      className={`h-8 px-2.5 inline-flex items-center gap-1 rounded-sm border border-slate-700 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:bg-slate-800 transition-colors ${className}`}>
      {children}
    </button>
  );
}

function PhotosBar({ task }) {
  const lu = task.localUpload;
  const total = task.num_photos || 0;
  const up = lu ? Math.max(0, lu.done - 2) : task.photo_upload?.done ?? task.uploaded_count ?? 0;
  const failed = lu?.failed ?? task.photo_upload?.failed ?? 0;
  const scanned = task.scan_done || 0;
  const pct = total ? Math.round((100 * up) / total) : 0;
  const scanPct = total ? Math.round((100 * scanned) / total) : 0;
  return (
    <div className="mt-3 rounded-sm border border-slate-800 bg-slate-950/50 p-2.5" data-testid={`preschool-photos-bar-${task.task_id}`}>
      <div className="flex justify-between text-[10px] font-mono text-slate-400 mb-1">
        <span>Photos uploaded {up}/{total}{failed ? ` · ${failed} failed` : ""}</span>
        <span className="text-cyan-300">face-scanned {scanned}/{total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden relative">
        <div className="absolute inset-y-0 left-0 bg-slate-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
        <div className="absolute inset-y-0 left-0 bg-cyan-400 transition-[width] duration-500" style={{ width: `${scanPct}%` }} />
      </div>
    </div>
  );
}

export function PreschoolTaskCard({ task, onReview, onView, onDownload, onLog, onRetry, onDelete, onApprove }) {
  const st = STATUS[task.status] || STATUS.queued;
  const Icon = st.icon;
  const pct = task.progress?.pct ?? 0;
  const id = task.task_id;
  const local = task.local;
  return (
    <div className="border border-slate-800 bg-slate-900/40 rounded-md p-4 hover:border-slate-700 transition-colors" data-testid={`preschool-task-${id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-slate-100 truncate" data-testid={`preschool-task-name-${id}`}>{task.name}</h3>
            <span className={`inline-flex items-center gap-1 px-2 h-5 rounded-sm text-[10px] font-bold uppercase tracking-widest ${st.cls}`} data-testid={`preschool-task-status-${id}`}>
              <Icon className={`h-3 w-3 ${st.spin ? "animate-spin" : ""}`} />{st.label}
            </span>
            {!task.is_own && task.owner_email && (
              <span className="px-2 h-5 inline-flex items-center rounded-sm text-[10px] bg-indigo-950/50 text-indigo-200 border border-indigo-800/60">{task.owner_email}</span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            {task.created_at ? fmt.format(new Date(task.created_at)) : "Preparing"} · {task.num_photos ?? 0} photos
            {task.num_people ? ` · ${task.num_people} people` : ""}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {["review", "waiting_photos"].includes(task.status) && (
            <IconBtn testId={`preschool-review-btn-${id}`} title="Check numbers read from the sheet" onClick={() => onReview(task)} className="border-amber-600 text-amber-200 hover:bg-amber-900/40">
              <ListChecks className="h-3.5 w-3.5" />Review
            </IconBtn>
          )}
          {task.status === "done" && (
            <>
              <IconBtn testId={`preschool-view-btn-${id}`} title="Browse by class" onClick={() => onView(task)}><Eye className="h-3.5 w-3.5" />View</IconBtn>
              <IconBtn testId={`preschool-numbers-btn-${id}`} title="Edit numbers and re-run" onClick={() => onReview(task)}><ListChecks className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn testId={`preschool-download-btn-${id}`} title="Download ZIP" onClick={() => onDownload(task)} className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/40">
                <Download className="h-3.5 w-3.5" />ZIP
              </IconBtn>
            </>
          )}
          {task.status === "match_review" && (
            <>
              <IconBtn testId={`preschool-view-btn-${id}`} title="Review matches by day and class" onClick={() => onView(task)} className="border-amber-600 text-amber-200 hover:bg-amber-900/40"><Eye className="h-3.5 w-3.5" />Review matches</IconBtn>
              <IconBtn testId={`preschool-numbers-btn-${id}`} title="Edit numbers and re-run" onClick={() => onReview(task)}><ListChecks className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn testId={`preschool-approve-btn-${id}`} title="Approve and build the ZIP" onClick={() => onApprove(task)} className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/40"><CheckCircle2 className="h-3.5 w-3.5" />Approve</IconBtn>
            </>
          )}
          {task.status === "failed" && (
            <IconBtn testId={`preschool-retry-btn-${id}`} title="Retry" onClick={() => onRetry(task)} className="border-cyan-700 text-cyan-300"><RotateCw className="h-3.5 w-3.5" />Retry</IconBtn>
          )}
          {!local && <IconBtn testId={`preschool-log-btn-${id}`} title="Download audit log" onClick={() => onLog(task)}><FileText className="h-3.5 w-3.5" /></IconBtn>}
          <IconBtn testId={`preschool-delete-btn-${id}`} title={task.status === "uploading" ? "Cancel upload & delete" : "Delete"} onClick={() => onDelete(task)} className="hover:text-red-300 hover:border-red-800">
            <Trash2 className="h-3.5 w-3.5" />
          </IconBtn>
        </div>
      </div>
      {["uploading", "running", "queued", "waiting"].includes(task.status) && (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-amber-400 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5 font-mono" data-testid={`preschool-task-progress-${id}`}>{task.progress?.message}</p>
        </div>
      )}
      {["review", "waiting_photos"].includes(task.status) && <p className="text-[11px] text-amber-200/80 mt-2">{task.progress?.message}</p>}
      {!local && task.photos_uploaded === false && <PhotosBar task={task} />}
      {task.status === "match_review" && <p className="text-[11px] text-amber-200/80 mt-2" data-testid={`preschool-task-paused-${id}`}>{task.progress?.message}</p>}
      {task.status === "done" && (
        <div className="flex gap-4 mt-3 text-[11px] font-mono" data-testid={`preschool-task-counts-${id}`}>
          <span className="text-emerald-300">{task.num_ok} named</span>
          <span className="text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{task.num_review} to review</span>
          <span className="text-slate-400">{task.num_unassigned} unassigned</span>
        </div>
      )}
      {task.status === "failed" && <p className="text-[11px] text-red-300 mt-2 font-mono break-all">{task.error}</p>}
    </div>
  );
}
